// :cow: Cosmose CONFIDENTIAL :iso:
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  changePasswordError,
  changePasswordRequested,
  changePasswordSuccess,
  loggedOut,
  loginError,
  loginFirstStepError,
  loginFirstStepSuccess,
  loginRequested,
  loginSuccess,
  resetPasswordError,
  resetPasswordRequested,
  resetPasswordSuccess,
  tokenRefreshed,
  userRestored,
} from './auth.actions';
import { SnackbarService } from '@coa/app/libs/services/snackbar.service';
import { Router } from '@angular/router';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { AppConfig } from '@coa/app/core/config/appConfig';
import { PreviousUrlService } from '@coa/app/libs/services/previous-url.service';
import { ClientsService } from '@coa/app/core/services/clients.service';
import { AuthService } from '@coa/app/libs/auth/auth-service/services/auth.service';

@Injectable()
export class AuthEffects {

  loginRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginRequested),
      mergeMap((action) => this.authService.login(action.username, action.password, action.code).pipe(
        map(response => {
          if (response.business_client_ids.length > 0) {
            return loginSuccess({
              accessToken: response.access_token,
              refreshToken: response.refresh_token,
              businessClientIds: response.business_client_ids,
            });
          } else {
            return loginError({error: marker('User has no clients assigned!')});
          }
        }),
        catchError((error) => {
          if (error.error.error_description === 'No 2FA verification code') {
            return of(loginFirstStepSuccess());
          }
          if (error.error.error_description === 'Invalid 2FA verification code') {
            return of(loginFirstStepError({error: error.error.error_description}));
          }
          return of(loginError({error: error.error.error_description}));
        }),
      )),
    ),
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginSuccess),
      tap((action) => {
        localStorage.setItem(AppConfig.accessToken, action.accessToken);
        localStorage.setItem(AppConfig.refreshToken, action.refreshToken);
        if (this.previousUrlService.previousUrl) {
          this.router.navigateByUrl(this.previousUrlService.previousUrl);
        } else if (action.businessClientIds.length === 1) {
          this.router.navigate(['/clients', action.businessClientIds[0], 'dashboard']);
        } else {
          this.router.navigate(['/select-client']);
        }
      }),
    ), {dispatch: false},
  );

  userRestored$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userRestored),
      tap((action) => {
        localStorage.setItem(AppConfig.accessToken, action.accessToken);
        localStorage.setItem(AppConfig.refreshToken, action.refreshToken);
      }),
    ), {dispatch: false},
  );

  tokenRefreshed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tokenRefreshed),
      tap((action) => {
        localStorage.setItem(AppConfig.accessToken, action.accessToken);
        localStorage.setItem(AppConfig.refreshToken, action.refreshToken);
      }),
    ), {dispatch: false},
  );

  loginError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginError),
      tap((action) => {
        this.snackbarService.open(action.error);
      }),
    ), {dispatch: false},
  );

  loginFirstStepError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginFirstStepError),
      tap((action) => {
        this.snackbarService.open(action.error);
      }),
    ), {dispatch: false},
  );

  loggedOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loggedOut),
      tap(() => {
        localStorage.clear();
        sessionStorage.clear();
        this.previousUrlService.previousUrl = '';
        this.clientsService.clearCache();
        this.router.navigate(['login']);
      }),
    ), {dispatch: false},
  );

  resetPasswordRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetPasswordRequested),
      mergeMap((action) => this.authService.resetPassword(action.username).pipe(
        map(() => resetPasswordSuccess({username: action.username})),
        catchError((response) => of(resetPasswordError({error: response.error.error_description}))),
      )),
    ),
  );

  resetPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetPasswordSuccess),
      tap(action => {
        this.router.navigate(['change-password'], {queryParams: {username: action.username}});
        this.snackbarService.open(marker('Email with temporary password has been sent!'));
      }),
    ), {dispatch: false},
  );

  resetPasswordError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetPasswordError),
      tap((action) => {
        this.snackbarService.open(action.error);
      }),
    ), {dispatch: false},
  );

  changePasswordRequested$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePasswordRequested),
      mergeMap((action) => this.authService.changePassword(action.username, action.currentPassword, action.newPassword).pipe(
        map(() => changePasswordSuccess()),
        catchError((response) => of(changePasswordError({error: response.error.message}))),
      )),
    ),
  );

  changePasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePasswordSuccess),
      tap(() => {
        this.router.navigate(['login']);
        this.snackbarService.open(marker('Password has been changed!'));
      }),
    ), {dispatch: false},
  );

  changePasswordError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePasswordError),
      tap(() => {
        this.snackbarService.open(marker('Provided temporary password is incorrect!'));
      }),
    ), {dispatch: false},
  );

  constructor(
    private actions$: Actions,
    private router: Router,
    private snackbarService: SnackbarService,
    private previousUrlService: PreviousUrlService,
    private authService: AuthService,
    private clientsService: ClientsService) {
  }
}
